<script setup lang="ts">
  import _ from "lodash";
  import { TalkRoom } from "~/entities/TalkRoom";
  import { PlanInfo } from "~/entities/PlanInfo";
  import { DraggableItem } from "~/entities/DraggableItem";
  import { useTalkRoomStore } from "~/stores/TalkRoomStore";
  import { useTalkRoomEditStore } from "~/stores/TalkRoomEditStore";
  import { useTalkRoomViewStore } from "~/stores/TalkRoomViewStore";
  import { useSnackBarStore } from "~/stores/SnackBarStore";
  import { TalkRoomDisplayOrder } from "~/entities/TalkRoomDisplayOrder";
  import { TALK_ROOM_GROUP_MAX } from "~/consts/TalkConfig";
  import { useHistoryStore } from "~/stores/HistoryStore";

  interface Props {
    planInfo: PlanInfo;
    talkRooms: TalkRoom[];
    selectedTalkRoom: TalkRoom | undefined;
    isLoadingDm?: boolean;
    isTest?: boolean; // テスト時のみ true にする
  }
  const props = defineProps<Props>();

  interface Emits {
    (event: "search", searchWord: string): void;
  }
  const emit = defineEmits<Emits>();

  // 権限周り制御
  const isOwner = computed<boolean>(() => props.planInfo.isOwner);

  // 閲覧権限
  const canView = (talkRoom: TalkRoom | undefined) => {
    return canViewTalkRoom(talkRoom, isOwner.value, props.planInfo?.isContract);
  };

  // store 準備
  const talkRoomStore = useTalkRoomStore();
  const talkRoomEditStore = useTalkRoomEditStore();
  const talkRoomViewStore = useTalkRoomViewStore();
  const snackBarStore = useSnackBarStore();
  const historyStore = useHistoryStore();

  // レスポンシブ対応
  const isSp = isSpDisplayRef();

  // コンポーネント表示更新用
  // key を変更することで各部の再描画を行う (本当はリアクティブにやりたいが、、苦肉の策)
  const groupTalksKey = ref(1);
  const dMTalksKey = ref(1);
  const archiveTalksKey = ref(1);

  // トークルームの変更を監視し、表示反映するために key を変更する
  watch(
    () => props.talkRooms,
    () => {
      groupTalksKey.value++;
      dMTalksKey.value++;
      archiveTalksKey.value++;
    },
    { deep: true },
  );

  const planId = props.planInfo.id;

  // 開閉状態初期化および更新監視
  const isGroupTalkOpen = computed<boolean>({
    get: () => talkRoomViewStore.isGroupTalkRoomListOpen(planId),
    set: value => talkRoomViewStore.setGroupTalkRoomListOpen(planId, value),
  });
  const isDMTalkOpen = computed<boolean>({
    get: () => talkRoomViewStore.isDMTalkRoomListOpen(planId),
    set: value => talkRoomViewStore.setDMTalkRoomListOpen(planId, value),
  });
  const isArchiveTalkOpen = computed<boolean>({
    get: () => talkRoomViewStore.isArchiveTalkRoomListOpen(planId),
    set: value => talkRoomViewStore.setArchiveTalkRoomListOpen(planId, value),
  });

  // 未読フィルタ初期化および更新監視。切り替え時に再描画も
  const onlyUnreadGroupTalks = computed<boolean>({
    get: () => talkRoomViewStore.onlyUnreadGroupTalkRooms(planId),
    set: value => {
      talkRoomViewStore.setOnlyUnreadGroupTalkRooms(planId, value);
      groupTalksKey.value++;
    },
  });
  const onlyUnreadDMTalks = computed<boolean>({
    get: () => talkRoomViewStore.onlyUnreadDMTalkRooms(planId),
    set: value => {
      talkRoomViewStore.setOnlyUnreadDMTalkRooms(planId, value);
      dMTalksKey.value++;
    },
  });

  const showTalkRoom = (roomId: string) => {
    // ルーム変更時にはqueryを変更して対応する
    const query = useRoute().query;
    // talk が設定されている場合は削除（存在しないトークにジャンプさせない）
    delete query.talk;
    historyStore.to({ query: { ...query, room: roomId } });
  };

  // トークチャンネル
  const onClickGroupTalkRoom = (roomId: string) => {
    // 既読にする
    groupTalkRooms.value.forEach(talkRoom => {
      if (talkRoom.id === roomId) {
        talkRoom.hasUnread = false;
      }
    });
    showTalkRoom(roomId);
  };
  const convertGroupTalkRoom = (talkRoom: TalkRoom) => {
    return {
      id: talkRoom.talkRoomId,
      label: talkRoom.roomName,
      canView: canView(talkRoom),
      hasUnread: talkRoom.hasUnread,
      imgPath: getTalkRoomIcon(talkRoom, props.planInfo),
      isUserIcon: false,
      isOwner: false,
      onClick: onClickGroupTalkRoom,
    } as DraggableItem;
  };
  const groupTalkRooms = computed<DraggableItem[]>(() =>
    props.talkRooms.filter(room => !room.isArchive && !room.isDirectMessage).map(convertGroupTalkRoom),
  );
  const groupTalkRoomsModel = computed<DraggableItem[]>({
    get: () => groupTalkRooms.value.filter(talkRoom => !onlyUnreadGroupTalks.value || talkRoom.hasUnread),
    set: async value => {
      // 並び順をDBに反映
      const displayOrders = value.map(item => {
        return new TalkRoomDisplayOrder(item.id, value.indexOf(item) + 1);
      });
      await talkRoomEditStore.setDisplayOrders(displayOrders);
    },
  });

  const isDraggingTalkRoom = ref(false);

  const clickNewGroupTalkRoom = () => {
    // 新規作成の場合はトークルームの最大数を超えていないかチェック
    if (talkRoomStore.isMaxGroupTalkRooms()) {
      snackBarStore.setError(`チャンネルは最大${TALK_ROOM_GROUP_MAX}個までです`);
      return;
    }

    // 現在のトークチャンネルのうち、最大の displayOrder + 1 を次の displayOrder として設定する
    const displayOrders = props.talkRooms.map((talkRoom: TalkRoom) => talkRoom.displayOrder);
    const nextDisplayOrder = Math.max(...displayOrders) + 1;
    talkRoomEditStore.init(nextDisplayOrder);
    historyStore.to(`/plan/${props.planInfo.id}/talk/create`);
  };

  // ダイレクトメッセージ
  const onClickDMTalkRoom = (roomId: string) => {
    // 既読にする
    dMTalkRooms.value.forEach(talkRoom => {
      if (talkRoom.id === roomId) {
        talkRoom.hasUnread = false;
      }
    });
    showTalkRoom(roomId);
  };
  const convertDmTalkRoom = (talkRoom: TalkRoom) => {
    return {
      id: talkRoom.talkRoomId,
      label: dMTalkRoomLabel(talkRoom, props.planInfo),
      canView: true,
      hasUnread: talkRoom.hasUnread,
      imgPath: getTalkRoomIcon(talkRoom, props.planInfo),
      isUserIcon: true,
      isOwner: !isOwner.value, // 講師はユーザー情報を、ユーザーは講師情報を見る
      onClick: onClickDMTalkRoom,
    } as DraggableItem;
  };
  const dMTalkRooms = computed<DraggableItem[]>(() =>
    props.talkRooms
      .filter(room => !room.isArchive && room.isDirectMessage)
      .sort((a, b) => {
        // 未読 x 最終投稿日降順 でソート
        if (!a.hasUnread && b.hasUnread) {
          return 1;
        } else if (a.hasUnread && !b.hasUnread) {
          return -1;
        }
        // 未投稿（最終投稿日がない）は最後尾に
        if (!a.latestPostDateTime && b.latestPostDateTime) {
          return 1;
        } else if (a.latestPostDateTime && !b.latestPostDateTime) {
          return -1;
        }
        return a.latestPostDateTime < b.latestPostDateTime ? 1 : -1;
      })
      .map(convertDmTalkRoom),
  );
  const dMTalkRoomsModel = computed<DraggableItem[]>(() => {
    // DM は手動ソートできないので getter のみ
    const unreadDMTalkRooms = dMTalkRooms.value.filter(talkRoom => talkRoom.hasUnread);
    if (onlyUnreadDMTalks.value) {
      return unreadDMTalkRooms;
    }
    // 未読・既読の順に連結して表示
    const readDMTalkRooms = dMTalkRooms.value.filter(talkRoom => !talkRoom.hasUnread);
    return unreadDMTalkRooms.concat(readDMTalkRooms);
  });
  const clickNewDMTalkRoom = () => {
    isTalkDmUserSearchModalOpen.value = true;
  };

  // アーカイブトークルーム
  const onClickArchiveTalkRoom = (roomId: string) => {
    // 既読にする
    archivedTalkRooms.value.forEach(talkRoom => {
      if (talkRoom.id === roomId) {
        talkRoom.hasUnread = false;
      }
    });
    showTalkRoom(roomId);
  };
  const convertArchiveTalkRoom = (talkRoom: TalkRoom) => {
    return talkRoom.isDirectMessage
      ? ({
          id: talkRoom.talkRoomId,
          label: dMTalkRoomLabel(talkRoom, props.planInfo),
          canView: true,
          hasUnread: talkRoom.hasUnread,
          imgPath: getTalkRoomIcon(talkRoom, props.planInfo),
          isUserIcon: true,
          isOwner: !isOwner.value, // 講師はユーザー情報を、ユーザーは講師情報を見る
          onClick: onClickArchiveTalkRoom,
        } as DraggableItem)
      : ({
          id: talkRoom.talkRoomId,
          label: talkRoom.roomName,
          canView: canView(talkRoom),
          hasUnread: talkRoom.hasUnread,
          imgPath: getTalkRoomIcon(talkRoom, props.planInfo),
          isUserIcon: false,
          isOwner: false,
          onClick: onClickArchiveTalkRoom,
        } as DraggableItem);
  };
  const archivedTalkRooms = computed<DraggableItem[]>(() =>
    props.talkRooms
      .filter(room => room.isArchive)
      .sort((a, b) => (a.archiveAt < b.archiveAt ? 1 : -1))
      .map(convertArchiveTalkRoom),
  );
  const archivedTalkRoomsModel = computed<DraggableItem[]>(() => {
    // ソートしないかつ未読フィルタないのでそのままの getter のみ
    return archivedTalkRooms.value;
  });

  // 各トークの未読状態
  const isGroupTalkHavingUnread = computed(() => {
    return groupTalkRooms.value.some(talkRoom => talkRoom.hasUnread);
  });
  const isDMTalkHavingUnread = computed(() => {
    return dMTalkRooms.value.some(talkRoom => talkRoom.hasUnread);
  });
  const isArchiveTalkHavingUnread = computed(() => {
    return archivedTalkRooms.value.some(talkRoom => talkRoom.hasUnread);
  });

  // DM ユーザー検索モーダル
  const isTalkDmUserSearchModalOpen = ref(false);

  // メッセージ検索モーダル
  const isTalkMessageSearchModalOpen = ref(false);

  // 検索ワード
  const searchWord = ref("");
  const doSearch = () => {
    // 検索ワード変更時または input クリック時呼ばれる
    if (canSearchTalk(searchWord.value)) {
      isTalkMessageSearchModalOpen.value = true;
      emit("search", searchWord.value);
    }
  };
  const debouncedSearch = _.debounce(doSearch, props.isTest ? 1 : 1000); // 1秒デバウンスして検索。テスト時は即時検索
  watch(searchWord, () => {
    // 検索ワード変更時はデバウンスして検索
    debouncedSearch();
  });
</script>

<template>
  <MoleculesContainerMenuList :margin-y="30">
    <div v-if="groupTalkRooms.length < 1 && isSp" class="mx-8 my-10">チャンネルがありません。</div>

    <template v-else>
      <v-text-field
        v-model="searchWord"
        class="search-input"
        placeholder="メッセージ検索"
        prepend-inner-icon="$mdiMagnify"
        variant="solo"
        :autofocus="false"
        hide-details="auto"
        density="compact"
        clearable
        @keydown.enter.prevent
        @click="doSearch"
      ></v-text-field>
    </template>

    <MoleculesExpansionPanel v-model:is-open="isGroupTalkOpen" :has-unread="isGroupTalkHavingUnread">
      <template #title>
        <div class="d-flex w-100" :class="isSp ? `pr-2` : `pr-1`">
          <div class="my-auto mr-auto d-flex" :class="isSp ? `panel-title-sp` : `panel-title`">
            <div :class="isSp ? `panel-title-label-sp` : `panel-title-label`">チャンネル</div>
            <v-icon
              v-if="isOwner"
              icon="$mdiPlus"
              class="add-talkroom-button"
              size="large"
              data-testid="add-talk-channel-button"
              @click.stop="clickNewGroupTalkRoom"
            />
          </div>
          <v-checkbox v-model="onlyUnreadGroupTalks" class="only-unread-checkbox" @click.stop></v-checkbox>
          <div class="my-auto mr-4 only-unread-label">未読</div>
        </div>
      </template>
      <template #default>
        <MoleculesDraggableList
          :key="groupTalksKey"
          v-model:items="groupTalkRoomsModel"
          v-model:is-dragging="isDraggingTalkRoom"
          :selected-id="selectedTalkRoom?.talkRoomId"
          :can-drag="!onlyUnreadGroupTalks && props.planInfo.isOwner"
        ></MoleculesDraggableList>
      </template>
    </MoleculesExpansionPanel>

    <MoleculesExpansionPanel v-model:is-open="isDMTalkOpen" :has-unread="isDMTalkHavingUnread">
      <template #title>
        <div class="d-flex w-100" :class="isSp ? `pr-2` : `pr-1`">
          <div class="my-auto mr-auto d-flex" :class="isSp ? `panel-title-sp` : `panel-title`">
            <div :class="isSp ? `panel-title-label-sp` : `panel-title-label`">DM</div>
            <v-icon
              v-if="isOwner"
              icon="$mdiPlus"
              class="add-talkroom-button"
              size="large"
              data-testid="add-talk-dm-button"
              @click.stop="clickNewDMTalkRoom"
            />
          </div>
          <v-checkbox v-model="onlyUnreadDMTalks" class="only-unread-checkbox" @click.stop></v-checkbox>
          <div class="my-auto mr-4 only-unread-label">未読</div>
        </div>
      </template>
      <template #default>
        <!-- DM を遅延取得する場合にローディング表示しておく -->
        <div v-if="isLoadingDm" class="d-flex justify-center">
          <AtomsLoadingSkeleton class="w-100 h-100" />
        </div>
        <MoleculesDraggableList
          v-else
          :key="dMTalksKey"
          v-model:items="dMTalkRoomsModel"
          :selected-id="selectedTalkRoom?.talkRoomId"
          :can-drag="false"
        ></MoleculesDraggableList>
      </template>
    </MoleculesExpansionPanel>

    <MoleculesExpansionPanel v-if="isOwner" v-model:is-open="isArchiveTalkOpen" :has-unread="isArchiveTalkHavingUnread">
      <template #title>
        <div :class="isSp ? `panel-title-sp` : `panel-title`">アーカイブ</div>
      </template>
      <template #default>
        <MoleculesDraggableList
          :key="archiveTalksKey"
          v-model:items="archivedTalkRoomsModel"
          :selected-id="selectedTalkRoom?.talkRoomId"
          :can-drag="false"
        ></MoleculesDraggableList>
      </template>
    </MoleculesExpansionPanel>
  </MoleculesContainerMenuList>

  <OrganismsTalkDmUserSearchModal
    v-if="isTalkDmUserSearchModalOpen"
    v-model:is-open="isTalkDmUserSearchModalOpen"
    :plan-info="props.planInfo"
    :talk-rooms="props.talkRooms"
  ></OrganismsTalkDmUserSearchModal>

  <OrganismsTalkMessageSearchModal
    v-if="isTalkMessageSearchModalOpen"
    v-model:is-open="isTalkMessageSearchModalOpen"
    v-model:search-word="searchWord"
    :plan-info="props.planInfo"
    :selected-talk-room-id="selectedTalkRoom?.talkRoomId"
    :talk-rooms="props.talkRooms"
  ></OrganismsTalkMessageSearchModal>
</template>

<style lang="scss" scoped>
  @import "assets/media.scss";

  .search-input {
    margin: 6px;
  }
  .panel-title {
    font-size: 12px;
  }
  .panel-title-sp {
    font-size: 15px;
  }
  .panel-title-label {
    width: 72px;
  }
  .panel-title-label-sp {
    width: 100px;
  }
  .add-talkroom-button {
    margin-top: -6px;
    margin-left: 8px;
    color: black;
    background-color: #a2a2a2;
    border-radius: 10px;
    cursor: pointer;
  }
  .only-unread-checkbox {
    width: 20px;
    font-size: 12px;
    margin: 4px 12px -20px -8px;
  }
  .only-unread-label {
    font-size: 12px;
  }
</style>
